import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, ChevronLeft, ChevronRight, Users, Eye, Building2, BarChart3, CreditCard, AlertTriangle, FileDown, Ticket } from "lucide-react";
import CompanyProfile from "@/pages/CompanyProfile";
import { payForJobPost } from "@/lib/razorpay";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import SearchableSelect from "@/components/SearchableSelect";
import SegmentedToggle from "@/components/SegmentedToggle";
import { EDUCATION_LEVELS } from "@/constants/education";

const TYPES = ["Full-time","Part-time","Daily Wage","Contract","Internship","Walk-in"];
const BIZ_TYPES = ["Company","Partnership Firm","Proprietorship","Local Shop","NGO","Government","Other"];
const BENEFITS = ["PF","ESI","Bonus","Food","Accommodation","Health Insurance","Travel Allowance"];

const statusClass = {
  pending: "badge-pending",
  approved: "badge-approved",
  rejected: "badge-rejected",
  expired: "badge-expired",
};

function MyJobs() {
  const [jobs, setJobs] = useState([]);
  const [pricing, setPricing] = useState([]);
  const navigate = useNavigate();
  const refresh = () => api.get("/jobs/my").then(r => setJobs(r.data));
  useEffect(() => { refresh(); api.get("/pricing").then(r => setPricing(r.data)); }, []);
  const priceFor = (cat) => pricing.find(x => x.category === cat)?.amount ?? 99;

  const daysLeft = (expires_at) => {
    if (!expires_at) return null;
    const ms = new Date(expires_at).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const renew = async (j) => {
    const amount = priceFor(j.category);
    const ok = window.confirm(`Renew "${j.title}" for another 50 days?\n\nCharge: ₹${amount} (${j.category})\n\nProceed?`);
    if (!ok) return;
    try {
      const result = await payForJobPost({ category: j.category });
      const fd = new FormData(); fd.append("payment_id", result.payment_id);
      await api.post(`/jobs/${j.id}/renew`, fd);
      toast.success("Renewed for 50 more days");
      refresh();
    } catch (e) { toast.error(e?.message || formatError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="my-jobs-table">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">My Job Posts</h2>
          <p className="text-slate-500 text-sm">Track status of your listings. Each post is valid for 50 days.</p>
        </div>
        <Link to="/dashboard/poster/new"><Button className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="post-new-job-btn"><Plus className="w-4 h-4 mr-1" /> Post New Job</Button></Link>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead><TableHead>City</TableHead><TableHead>Type</TableHead>
              <TableHead>Status</TableHead><TableHead>Expires</TableHead><TableHead>Views</TableHead><TableHead>Applicants</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No jobs yet. Post your first one!</TableCell></TableRow>}
            {jobs.map(j => {
              const dl = daysLeft(j.expires_at);
              return (
                <TableRow key={j.id}>
                  <TableCell className="font-medium">{j.title}<div className="text-xs text-slate-500">{j.business_name}</div>
                    {j.rejection_reason && <div className="text-xs text-red-600 mt-1">Reason: {j.rejection_reason}</div>}
                  </TableCell>
                  <TableCell>{j.city}</TableCell>
                  <TableCell>{j.job_type}</TableCell>
                  <TableCell><Badge className={statusClass[j.status] || ""}>{j.status}</Badge></TableCell>
                  <TableCell>
                    {j.status === "expired" ? <span className="text-red-600 text-xs font-medium">Expired</span>
                      : dl !== null && dl <= 7 ? <span className="text-amber-600 text-xs font-medium" data-testid={`expiring-${j.id}`}>{dl} day{dl !== 1 ? "s" : ""} left</span>
                      : dl !== null ? <span className="text-xs text-slate-600">{dl} days left</span>
                      : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                  <TableCell><span className="flex items-center gap-1 text-slate-600"><Eye className="w-3 h-3" />{j.views || 0}</span></TableCell>
                  <TableCell>{j.applications_count || 0}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {(j.status === "expired" || (dl !== null && dl <= 7)) && (
                      <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => renew(j)} data-testid={`renew-${j.id}`}>
                        Renew (50 days)
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/poster/applicants/${j.id}`)} data-testid={`applicants-${j.id}`}><Users className="w-3 h-3 mr-1" />Applicants</Button>
                    <Button size="sm" variant="ghost" onClick={() => del(j.id)} data-testid={`delete-job-${j.id}`}>Delete</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PostJob() {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [company, setCompany] = useState(null);
  const [companyLoaded, setCompanyLoaded] = useState(false);
  const [postingStatus, setPostingStatus] = useState({ first_free_used: false, total_posts: 0 });
  const [pricing, setPricing] = useState([]);

  // ── coupon / payment state ──
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);   // { code, base_amount, discount, final_amount, is_free }
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [paying, setPaying] = useState(false);

  const [form, setForm] = useState({
    title: "", category: "", city: "", job_type: "Full-time", vacancies: 1,
    salary_type: "monthly", salary_min: 15000, salary_max: 25000, currency: "INR",
    description: "", requirements: "",
    experience_disclosed: true, experience_min: 0, experience_max: 3, experience: "",
    education: "",
    location_type: "onsite",
    urgency: "", fresher_option: "",
    working_hours: "", benefits: [], deadline: "",
    apply_method: "portal", apply_contact: "", walkin_address: "",
    business_name: "", business_type: "Company", contact_person: "",
    business_logo: "", poster_email: "", mobile: "",
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => {
    api.get("/cities").then(r => setCities(r.data));
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/auth/me").then(r => update("poster_email", r.data.email));
    api.get("/companies/my").then(r => { setCompany(r.data); setCompanyLoaded(true); if (r.data) update("business_name", r.data.name); });
    api.get("/payments/posting-status").then(r => setPostingStatus(r.data));
    api.get("/pricing").then(r => setPricing(r.data));
  }, []);

  const priceFor = (cat) => {
    const p = pricing.find(x => x.category === cat);
    return p ? p.amount : 99;
  };

  // Whether this post requires payment (first post is free)
  const isPaid = postingStatus.first_free_used;
  const stepLabels = isPaid
    ? ["Job Info", "Business Info", "Review", "Payment"]
    : ["Job Info", "Business Info", "Review"];
  const lastStep = stepLabels.length;

  // Pricing breakdown (used on Payment step + dialog + button)
  const baseAmount = couponInfo?.base_amount ?? (form.category ? priceFor(form.category) : 0);
  const discount = couponInfo?.discount ?? 0;
  const total = couponInfo ? couponInfo.final_amount : baseAmount;

  // If category/city changes, a previously-applied coupon may no longer be valid → reset it
  useEffect(() => {
    setCouponInfo(null);
    setCouponError("");
  }, [form.category, form.city]);

  const applyCoupon = async () => {
    if (!form.category) { toast.error("Pick a job category first"); return; }
    if (!couponCode.trim()) { toast.error("Enter a coupon code"); return; }
    setApplyingCoupon(true);
    setCouponError("");
    try {
      const { data } = await api.post("/payments/apply-coupon", {
        code: couponCode.trim().toUpperCase(),
        category: form.category,
        city: form.city,
      });
      setCouponInfo(data);
      toast.success(`Coupon applied — you save ₹${data.discount}`);
    } catch (e) {
      setCouponInfo(null);
      setCouponError(formatError(e));
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => { setCouponInfo(null); setCouponCode(""); setCouponError(""); };

  // Free post → just create the job
  const submitFree = async () => {
    try {
      await api.post("/jobs", { ...form, payment_id: "" });
      toast.success("Submitted — pending admin review");
      navigate("/dashboard/poster");
    } catch (e) { toast.error(formatError(e)); }
  };

  // Paid post → pay (with coupon applied) then create the job
  const handlePayAndSubmit = async () => {
    setPaying(true);
    try {
      const result = await payForJobPost({
        category: form.category,
        city: form.city,
        couponCode: couponInfo?.code || "",
        prefill: { email: form.poster_email, contact: form.mobile },
      });
      await api.post("/jobs", { ...form, payment_id: result.payment_id });
      toast.success(total <= 0 ? "Submitted — pending admin review" : "Payment successful — job submitted for review");
      navigate("/dashboard/poster");
    } catch (e) {
      toast.error(e?.message || formatError(e));
    } finally {
      setPaying(false);
    }
  };

  // Validity popup state
  const [showValidity, setShowValidity] = useState(false);
  const cityRequired = form.location_type === "onsite";
  const onFinalClick = () => {
    if (!form.title || !form.category || (cityRequired && !form.city) || !form.description) {
      toast.error("Please fill all required fields in Step 1");
      return;
    }
    setShowValidity(true);
  };
  const confirmAndSubmit = async () => {
    setShowValidity(false);
    if (isPaid) await handlePayAndSubmit();
    else await submitFree();
  };

  const goNext = () => {
    // Validate before leaving Review → Payment
    if (step === 3 && (!form.title || !form.category || (cityRequired && !form.city) || !form.description)) {
      toast.error("Please fill all required fields in Step 1");
      return;
    }
    setStep(s => Math.min(lastStep, s + 1));
  };

  const toggleBenefit = (b) => update("benefits", form.benefits.includes(b) ? form.benefits.filter(x => x !== b) : [...form.benefits, b]);

  // ---- Gating UI: company missing / pending / rejected ----
  if (companyLoaded && !company) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="company-required-block">
        <Building2 className="w-12 h-12 text-[#1D6FF2] mx-auto mb-3" />
        <h3 className="text-xl font-bold">Create your Company Profile first</h3>
        <p className="text-slate-600 mt-2 mb-5 max-w-md mx-auto">You need an approved company profile before posting jobs. It only takes a minute.</p>
        <Link to="/dashboard/poster/company"><Button className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white">Create Company</Button></Link>
      </div>
    );
  }
  if (companyLoaded && company && company.status !== "approved") {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center" data-testid="company-pending-block">
        <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
        <h3 className="text-xl font-bold">Company is {company.status}</h3>
        <p className="text-slate-700 mt-2 mb-5 max-w-md mx-auto">
          {company.status === "pending" ? "Once an admin approves your company, you can post jobs." : `Reason: ${company.rejection_reason}. Update your profile to re-submit.`}
        </p>
        <Link to="/dashboard/poster/company"><Button variant="outline">Edit Company</Button></Link>
      </div>
    );
  }

  return (
    <div data-testid="post-job-wizard">
      <h2 className="text-2xl font-bold mb-2">Post a New Job</h2>
      {!isPaid ? (
        <div className="mb-6 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800" data-testid="first-free-banner">
          🎉 <b>Your first job post is free!</b> Subsequent posts will be charged per category (₹99 - ₹299).
        </div>
      ) : (
        <div className="mb-6 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900" data-testid="paid-banner">
          You've used your free post. This post will be ₹{form.category ? priceFor(form.category) : "—"} {form.category && `(${form.category})`}. Apply a coupon at the Payment step for a discount.
        </div>
      )}
      {/* Stepper */}
      <div className="flex gap-2 mb-8">
        {stepLabels.map((label, i) => (
          <div key={label} className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium ${step === i+1 ? "border-[#1D6FF2] bg-[#E8F0FE] text-[#1D6FF2]" : "border-slate-200 text-slate-400"}`}>
            Step {i+1}: {label}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Job Title *</Label><Input value={form.title} onChange={e => update("title", e.target.value)} data-testid="job-title-input" /></div>
            <div><Label>Job Type *</Label>
              <Select value={form.job_type} onValueChange={v => update("job_type", v)}>
                <SelectTrigger data-testid="job-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Category *</Label>
              <Select value={form.category} onValueChange={v => update("category", v)}>
                <SelectTrigger data-testid="job-category-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Work Location</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  { label: "Onsite (pick a city)", value: "onsite" },
                  { label: "Online", value: "online" },
                  { label: "Work From Home (WFH)", value: "wfh" },
                  { label: "Not Disclosed", value: "not_disclosed" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => update("location_type", opt.value)}
                    className={`px-4 py-1.5 rounded-full border text-sm font-medium transition ${
                      form.location_type === opt.value
                        ? "border-[#1D6FF2] bg-[#E8F0FE] text-[#1D6FF2]"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {form.location_type === "onsite" && (
              <div><Label>City *</Label>
                <Select value={form.city} onValueChange={v => update("city", v)}>
                  <SelectTrigger data-testid="job-city-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{cities.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Vacancies</Label><Input type="number" value={form.vacancies} onChange={e => update("vacancies", parseInt(e.target.value || "1"))} /></div>
            <div className="md:col-span-2">
              <Label>Hiring urgency (optional)</Label>
              <div className="mt-2">
                <SegmentedToggle
                  options={[
                    { value: "urgent", label: "Urgent Hiring" },
                    { value: "immediate", label: "Immediate Hiring" },
                  ]}
                  value={form.urgency}
                  onChange={v => update("urgency", v)}
                  allowDeselect
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Freshers</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  { label: "No fresher preference", value: "" },
                  { label: "Freshers can also apply", value: "freshers_can_apply" },
                  { label: "Only freshers can apply", value: "freshers_only" },
                ].map(opt => (
                  <button
                    key={opt.value || "none"}
                    type="button"
                    onClick={() => update("fresher_option", opt.value)}
                    className={`px-4 py-1.5 rounded-full border text-sm font-medium transition ${
                      form.fresher_option === opt.value
                        ? "border-[#1D6FF2] bg-[#E8F0FE] text-[#1D6FF2]"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
  <Label>Salary</Label>
  <div className="flex flex-wrap gap-2 mt-2 mb-3">
    {[
      { label: "Monthly (₹)", value: "monthly" },
      { label: "Yearly (LPA)", value: "yearly" },
      { label: "Not Disclosed", value: "hidden" },
    ].map(opt => (
      <button
        key={opt.value}
        type="button"
        onClick={() => update("salary_type", opt.value)}
        className={`px-4 py-1.5 rounded-full border text-sm font-medium transition ${
          form.salary_type === opt.value
            ? "border-[#1D6FF2] bg-[#E8F0FE] text-[#1D6FF2]"
            : "border-slate-200 text-slate-600 hover:border-slate-300"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
  {form.salary_type === "monthly" && (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label className="text-xs text-slate-500">Min (₹/month)</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
          <Input type="number" className="pl-7" value={form.salary_min} onChange={e => update("salary_min", parseFloat(e.target.value || "0"))} placeholder="e.g. 15000" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Max (₹/month)</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
          <Input type="number" className="pl-7" value={form.salary_max} onChange={e => update("salary_max", parseFloat(e.target.value || "0"))} placeholder="e.g. 25000" />
        </div>
      </div>
    </div>
  )}
  {form.salary_type === "yearly" && (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label className="text-xs text-slate-500">Min (LPA)</Label>
        <div className="relative">
          <Input type="number" step="0.1" value={form.salary_min} onChange={e => update("salary_min", parseFloat(e.target.value || "0"))} placeholder="e.g. 3" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">LPA</span>
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-500">Max (LPA)</Label>
        <div className="relative">
          <Input type="number" step="0.1" value={form.salary_max} onChange={e => update("salary_max", parseFloat(e.target.value || "0"))} placeholder="e.g. 6" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">LPA</span>
        </div>
      </div>
    </div>
  )}
  {form.salary_type === "hidden" && (
    <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>Salary will show as "Not Disclosed" to job seekers. Listings without a salary range tend to get fewer views — consider adding one if you can.</span>
    </div>
  )}
</div>
            <div className="md:col-span-2">
              <Label>Experience Required</Label>
              <div className="flex flex-wrap gap-2 mt-2 mb-3">
                {[
                  { label: "Specify range", value: true },
                  { label: "Not Disclosed", value: false },
                ].map(opt => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => update("experience_disclosed", opt.value)}
                    className={`px-4 py-1.5 rounded-full border text-sm font-medium transition ${
                      form.experience_disclosed === opt.value
                        ? "border-[#1D6FF2] bg-[#E8F0FE] text-[#1D6FF2]"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {form.experience_disclosed ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Min (years)</Label>
                    <Input type="number" min="0" step="1" value={form.experience_min ?? 0}
                      onChange={e => update("experience_min", parseFloat(e.target.value || "0"))} placeholder="e.g. 0" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Max (years) — leave blank for "X+ years"</Label>
                    <Input type="number" min="0" step="1" value={form.experience_max ?? ""}
                      onChange={e => update("experience_max", e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="e.g. 3" />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2">
                  Experience will show as "Not Disclosed" to job seekers.
                </p>
              )}
            </div>
            <div>
              <Label>Minimum Education</Label>
              <SearchableSelect
                value={form.education || "any"}
                onChange={v => update("education", v === "any" ? "" : v)}
                clearValue="any"
                clearLabel="Not specified"
                searchPlaceholder="Search education..."
                options={EDUCATION_LEVELS.map(e => ({ value: e, label: e }))}
                triggerClassName="h-10 px-3 rounded-md border border-slate-200 bg-white"
              />
            </div>
            <div><Label>Working Hours / Shift</Label><Input value={form.working_hours} onChange={e => update("working_hours", e.target.value)} placeholder="e.g. 9 AM – 6 PM, Mon–Sat" /></div>
            <div><Label>Application Deadline</Label><Input type="date" value={form.deadline || ""} onChange={e => update("deadline", e.target.value)} /></div>
          </div>
          <div><Label>Job Description *</Label><Textarea rows={5} value={form.description} onChange={e => update("description", e.target.value)} data-testid="job-desc-input" /></div>
          <div><Label>Requirements / Skills</Label><Textarea rows={3} value={form.requirements} onChange={e => update("requirements", e.target.value)} /></div>
          <div>
            <Label>Benefits</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {BENEFITS.map(b => (
                <label key={b} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.benefits.includes(b)} onCheckedChange={() => toggleBenefit(b)} /> {b}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Business / Shop Name *</Label><Input value={form.business_name} onChange={e => update("business_name", e.target.value)} data-testid="biz-name-input" /></div>
            <div><Label>Business Type</Label>
              <Select value={form.business_type} onValueChange={v => update("business_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{BIZ_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={e => update("contact_person", e.target.value)} /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={e => update("mobile", e.target.value)} /></div>
            <div><Label>Email (auto)</Label><Input value={form.poster_email} disabled /></div>
            <div><Label>How to Apply</Label>
              <Select value={form.apply_method} onValueChange={v => update("apply_method", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portal">Through Portal</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="walkin">Walk-in</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.apply_method === "whatsapp" && <div><Label>WhatsApp Number</Label><Input value={form.apply_contact} onChange={e => update("apply_contact", e.target.value)} placeholder="+91..." /></div>}
            {form.apply_method === "email" && <div><Label>Email</Label><Input value={form.apply_contact} onChange={e => update("apply_contact", e.target.value)} /></div>}
            {form.apply_method === "walkin" && <div className="md:col-span-2"><Label>Walk-in Address</Label><Textarea value={form.walkin_address} onChange={e => update("walkin_address", e.target.value)} /></div>}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h3 className="font-semibold">Review your job post</h3>
          <div className="text-sm text-slate-700 space-y-1">
            <div><b>Title:</b> {form.title}</div>
            <div>
              <b>Category:</b> {form.category} ·{" "}
              <b>Location:</b>{" "}
              {form.location_type === "onsite" ? form.city
                : form.location_type === "online" ? "Online"
                : form.location_type === "wfh" ? "Work From Home (WFH)"
                : "Not Disclosed"} ·{" "}
              <b>Type:</b> {form.job_type}
            </div>
            <div><b>Business:</b> {form.business_name} ({form.business_type})</div>
            <div>
              <b>Salary:</b>{" "}
              {form.salary_type === "monthly" && `₹${form.salary_min?.toLocaleString()} – ₹${form.salary_max?.toLocaleString()} /month`}
              {form.salary_type === "yearly" && `${form.salary_min} – ${form.salary_max} LPA`}
              {form.salary_type === "hidden" && "Not Disclosed"}
            </div>
            <div>
              <b>Experience:</b>{" "}
              {form.experience_disclosed
                ? `${form.experience_min ?? 0}${form.experience_max != null ? `–${form.experience_max}` : "+"} years`
                : "Not Disclosed"}
              {form.education && <> · <b>Education:</b> {form.education}</>}
            </div>
            {(form.urgency || form.fresher_option) && (
              <div>
                {form.urgency === "urgent" && <>Marked as <b>Urgent Hiring</b></>}
                {form.urgency === "immediate" && <>Marked as <b>Immediate Hiring</b></>}
                {form.urgency && form.fresher_option && " · "}
                {form.fresher_option === "freshers_can_apply" && "Freshers can also apply"}
                {form.fresher_option === "freshers_only" && "Only freshers can apply"}
              </div>
            )}
            <div><b>Apply via:</b> {form.apply_method} {form.apply_contact}</div>
            <div className="pt-2 whitespace-pre-wrap"><b>Description:</b> {form.description}</div>
          </div>
          {isPaid && (
            <div className="mt-2 text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              Next step is payment. This post costs ₹{form.category ? priceFor(form.category) : "—"} — you can apply a coupon there.
            </div>
          )}
        </div>
      )}

      {isPaid && step === 4 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="payment-step">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#1D6FF2]" /> Payment</h3>
            <p className="text-slate-500 text-sm">Your first post was free. This post is charged for the <b>{form.category}</b> category.</p>
          </div>

          {/* Coupon */}
          <div className="rounded-xl border border-slate-200 p-4">
            <Label className="text-sm font-medium flex items-center gap-1.5"><Ticket className="w-4 h-4 text-[#1D6FF2]" /> Have a coupon code?</Label>
            {!couponInfo ? (
              <div className="flex gap-2 mt-2">
                <Input
                  value={couponCode}
                  onChange={e => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  className="font-mono uppercase"
                  onKeyDown={e => e.key === "Enter" && applyCoupon()}
                  data-testid="coupon-input"
                />
                <Button onClick={applyCoupon} disabled={applyingCoupon || !couponCode.trim()} variant="outline" data-testid="apply-coupon-btn">
                  {applyingCoupon ? "Checking…" : "Apply"}
                </Button>
              </div>
            ) : (
              <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <div className="text-sm text-green-800">
                  <b className="font-mono">{couponInfo.code}</b> applied — you save ₹{couponInfo.discount}
                </div>
                <button onClick={removeCoupon} className="text-xs text-red-600 hover:underline" data-testid="remove-coupon-btn">Remove</button>
              </div>
            )}
            {couponError && <p className="text-xs text-red-600 mt-1.5">{couponError}</p>}
          </div>

          {/* Summary */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2 text-sm" data-testid="payment-summary">
            <div className="flex justify-between">
              <span className="text-slate-600">Job posting ({form.category})</span>
              <span>₹{baseAmount}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Coupon discount{couponInfo?.code ? ` (${couponInfo.code})` : ""}</span>
                <span>− ₹{discount}</span>
              </div>
            )}
            <div className="border-t border-slate-200 pt-2 flex justify-between font-bold text-base">
              <span>Total payable</span>
              <span>{total <= 0 ? <span className="text-green-600">FREE</span> : `₹${total}`}</span>
            </div>
          </div>

          {total <= 0 && couponInfo && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              🎉 This coupon covers the full amount — no payment needed. Click below to submit.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}><ChevronLeft className="w-4 h-4" /> Back</Button>
        {step < lastStep ? (
          <Button onClick={goNext} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="next-step-btn">Next <ChevronRight className="w-4 h-4" /></Button>
        ) : (
          <Button onClick={onFinalClick} disabled={paying} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="submit-job-btn">
            {isPaid
              ? (paying ? "Processing…" : total <= 0 ? "Submit (Free with coupon)" : `Pay ₹${total} & Submit`)
              : "Submit for Review"}
          </Button>
        )}
      </div>

      <Dialog open={showValidity} onOpenChange={setShowValidity}>
        <DialogContent data-testid="validity-dialog">
          <DialogHeader>
            <DialogTitle>📌 Job Validity: 50 Days</DialogTitle>
            <DialogDescription>Please read before submitting your job post.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-[#E8F0FE] border border-[#1D6FF2]/30 p-4">
              <p className="font-semibold text-slate-900 mb-1">Your job will be live for <span className="text-[#1D6FF2]">50 days</span> from approval.</p>
              <p className="text-slate-700">After 50 days, the listing will automatically expire and stop appearing in search results.</p>
            </div>
            <div className="space-y-1 text-slate-700">
              <p>• To keep the same job live beyond 50 days, you can <b>renew</b> it from <b>My Jobs</b>.</p>
              <p>• <b>Renewal requires payment</b> at the same per-category rate as new posts.</p>
              <p>• You'll receive a notification before expiry so you can renew on time.</p>
              {isPaid && (
                <p className="pt-2 text-amber-700">
                  ⚠ This post will be charged ₹{total}{discount > 0 ? ` (₹${discount} off with ${couponInfo.code})` : ""} for {form.category}.
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="outline" onClick={() => setShowValidity(false)} data-testid="validity-cancel-btn">Cancel</Button>
            <Button onClick={confirmAndSubmit} disabled={paying} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="validity-confirm-btn">
              {isPaid ? (total <= 0 ? "I Understand — Submit" : "I Understand — Pay & Submit") : "I Understand — Submit"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Applicants() {
  const path = window.location.pathname.split("/");
  const jobId = path[path.length - 1];
  const [apps, setApps] = useState([]);
  const [resumeUrl, setResumeUrl] = useState(null);
  useEffect(() => { api.get(`/applications/job/${jobId}`).then(r => setApps(r.data)); }, [jobId]);
  const updateStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    await api.put(`/applications/${id}/status`, fd);
    setApps(apps.map(a => a.id === id ? { ...a, status } : a));
    toast.success("Status updated");
  };
  return (
    <div data-testid="applicants-page">
      <h2 className="text-2xl font-bold mb-6">Applicants</h2>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Skills · Exp</TableHead><TableHead>Resume</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {apps.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No applicants yet</TableCell></TableRow>}
            {apps.map(a => (
              <TableRow key={a.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {a.user_photo && <img src={`${process.env.REACT_APP_BACKEND_URL}${a.user_photo}`} alt="" className="w-8 h-8 rounded-full object-cover" />}
                    <div>
                      <div className="font-medium">{a.user_name}</div>
                      <div className="text-xs text-slate-500">{a.user_city}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  <div>{a.user_email}</div>
                  {a.user_mobile && <div className="text-xs">{a.user_mobile}</div>}
                </TableCell>
                <TableCell className="text-sm">
                  <div className="text-slate-700 truncate max-w-[200px]">{(a.skills || []).slice(0,3).join(", ")}</div>
                  <div className="text-xs text-slate-500">{a.experience_years || 0} yrs</div>
                </TableCell>
                <TableCell>
                  {a.resume_url ? (
                    <Button size="sm" variant="outline" onClick={() => setResumeUrl(`${process.env.REACT_APP_BACKEND_URL}${a.resume_url}`)} data-testid={`view-resume-${a.id}`}>
                      <FileDown className="w-3 h-3 mr-1" /> View
                    </Button>
                  ) : <span className="text-xs text-slate-400">None</span>}
                </TableCell>
                <TableCell><Badge>{a.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Select value={a.status} onValueChange={v => updateStatus(a.id, v)}>
                    <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewed">Viewed</SelectItem>
                      <SelectItem value="under_review">Under Review</SelectItem>
                      <SelectItem value="shortlisted">Shortlisted</SelectItem>
                      <SelectItem value="hired">Hired</SelectItem>
                      <SelectItem value="not_selected">Not Selected</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!resumeUrl} onOpenChange={(o) => !o && setResumeUrl(null)}>
        <DialogContent className="max-w-4xl h-[85vh] p-0">
          <DialogHeader className="p-4 border-b"><DialogTitle>Resume</DialogTitle></DialogHeader>
          {resumeUrl && <iframe src={resumeUrl} title="Resume" className="w-full h-full" data-testid="resume-iframe" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stats() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/poster/stats").then(r => setData(r.data)); }, []);
  if (!data) return <div className="skeleton h-64 rounded-xl" />;
  const t = data.totals;
  return (
    <div data-testid="poster-stats">
      <h2 className="text-2xl font-bold mb-6">Posting Statistics</h2>
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Total Jobs</div><div className="text-3xl font-bold">{t.jobs}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Total Views</div><div className="text-3xl font-bold">{t.views}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="text-sm text-slate-500">Applications</div><div className="text-3xl font-bold">{t.applications}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-sm text-slate-500">Status</div>
          <div className="text-xs mt-1 space-x-2">
            <Badge className="badge-approved">{t.approved} live</Badge>
            <Badge className="badge-pending">{t.pending} pending</Badge>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold mb-3">Views by City</h3>
          {data.views_by_city.length === 0 ? <p className="text-sm text-slate-500">No data yet</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.views_by_city}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1D6FF2" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold mb-3">Applicants by City</h3>
          {data.applicants_by_city.length === 0 ? <p className="text-sm text-slate-500">No data yet</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.applicants_by_city}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#16a34a" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 mt-6">
        <h3 className="font-semibold mb-3">Trending Jobs (your top 5 by applications)</h3>
        {data.trending_jobs.length === 0 ? <p className="text-sm text-slate-500">No data yet</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Views</TableHead><TableHead>Applications</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.trending_jobs.map(j => (
                <TableRow key={j.id}><TableCell className="font-medium">{j.title}</TableCell><TableCell>{j.views}</TableCell><TableCell>{j.applications}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function Payments() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/payments/mine").then(r => setItems(r.data)); }, []);
  return (
    <div data-testid="payments-page">
      <h2 className="text-2xl font-bold mb-6">Payment History</h2>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Category</TableHead><TableHead>Coupon</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No payments yet — your first post was free!</TableCell></TableRow>}
            {items.map(p => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-xs">{p.order_id}</TableCell>
                <TableCell>{p.category}</TableCell>
                <TableCell className="text-sm">
                  {p.coupon_code
                    ? <span className="font-mono text-green-700">{p.coupon_code}{p.discount ? ` (−₹${p.discount})` : ""}</span>
                    : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell>₹{p.amount}</TableCell>
                <TableCell><Badge className={p.status === "paid" ? "badge-approved" : p.status === "failed" ? "badge-rejected" : "badge-pending"}>{p.status}</Badge></TableCell>
                <TableCell className="text-sm text-slate-500">{(p.created_at || "").slice(0,10)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function PosterDashboard() {
  const location = useLocation();
  const tab = (path) => location.pathname === path ? "bg-[#E8F0FE] text-[#1D6FF2]" : "text-slate-700 hover:bg-slate-100";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-[220px_1fr] gap-6" data-testid="poster-dashboard">
      <aside className="md:sticky md:top-20 md:self-start">
        <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1">
          <Link to="/dashboard/poster" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/poster")}`} data-testid="tab-my-jobs"><FileText className="w-4 h-4 inline mr-2" />My Jobs</Link>
          <Link to="/dashboard/poster/new" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/poster/new")}`} data-testid="tab-new-job"><Plus className="w-4 h-4 inline mr-2" />Post Job</Link>
          <Link to="/dashboard/poster/stats" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/poster/stats")}`} data-testid="tab-stats"><BarChart3 className="w-4 h-4 inline mr-2" />Statistics</Link>
          <Link to="/dashboard/poster/company" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/poster/company")}`} data-testid="tab-company"><Building2 className="w-4 h-4 inline mr-2" />Company</Link>
          <Link to="/dashboard/poster/payments" className={`block px-3 py-2 rounded-lg text-sm font-medium ${tab("/dashboard/poster/payments")}`} data-testid="tab-payments"><CreditCard className="w-4 h-4 inline mr-2" />Payments</Link>
        </div>
      </aside>
      <Routes>
        <Route index element={<MyJobs />} />
        <Route path="new" element={<PostJob />} />
        <Route path="stats" element={<Stats />} />
        <Route path="company" element={<CompanyProfile />} />
        <Route path="payments" element={<Payments />} />
        <Route path="applicants/:jobId" element={<Applicants />} />
      </Routes>
    </div>
  );
}